// ===========================================
// Family Payment History
// ===========================================
// The admin family-details "Payment history" section: invoice payments merged
// with the family's one-off registration fee, a lifetime registration-status
// line (never "Unpaid"), a year filter + total, and record/amend/delete of a
// manual registration payment plus waive/revoke of the fee.
// md/plan/family-payment-history.md Task 4.2.

import { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Chip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import { usePermissions } from '../../../hooks/usePermissions';
import { useSchoolTimezone } from '../../../contexts/TenantContext';
import { schoolTodayUtcMidnight } from '../../../utils/dateUtils';
import { useFamilyPaymentHistory, useDeleteRegistrationPayment, useRevokeRegistrationFeeWaiver } from '../../../hooks/useFamilyPayments';
import type { FamilyPaymentRow, RegistrationFeeView } from '../../../api/familyPayments.api';
import { PAYMENT_METHOD_LABELS, INVOICE_STATUS_COLORS, formatPaymentDay, formatRecordedAt, invoiceStatusLabel } from '../../../utils/invoiceDisplay';
import { formatCurrencyFromDollars, paymentDetailText } from '../../../utils/currency';
import { apiErrorCode, apiErrorDetails, apiErrorMessage } from '../../../utils/apiError';
import ConfirmDialog from '../../common/ConfirmDialog';
import RegistrationStatusLine from './RegistrationStatusLine';
import RegistrationFeesList from './RegistrationFeesList';
import AssignRegistrationFeeDialog from './AssignRegistrationFeeDialog';
import { ClearRefundFlagDialog, CloseAsRefundedDialog, ForfeitRegistrationFeeDialog, ReverseForfeitDialog } from './ForfeitRegistrationFeeDialog';
import RecordRegistrationPaymentDialog from './RecordRegistrationPaymentDialog';
import WaiveRegistrationFeeDialog from './WaiveRegistrationFeeDialog';
import LateFeeExemptionLine from './LateFeeExemptionLine';

interface FamilyPaymentHistoryProps {
  familyId: string | undefined;
}

/** Who took the money. Account credit (#642) is neither: it moved from the family's balance. */
function paymentSourceLabel(payment: FamilyPaymentRow): string {
  if (payment.method === 'ACCOUNT_CREDIT') return 'Credit';
  return payment.isManual ? 'Admin' : 'Stripe';
}

export default function FamilyPaymentHistory({ familyId }: FamilyPaymentHistoryProps) {
  const { can, isOwner } = usePermissions();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const timeZone = useSchoolTimezone();
  const currentYear = schoolTodayUtcMidnight(timeZone).getUTCFullYear();
  const [year, setYear] = useState(currentYear);

  const canRead = can('operations_invoices'); // defaults to the 'read' action
  const canWrite = can('operations_invoices', 'write');
  const canDelete = can('operations_invoices', 'delete');

  const { data, isLoading, isError, isFetching, isPlaceholderData, refetch } = useFamilyPaymentHistory(
    familyId,
    year,
    canRead
  );

  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [paymentToEdit, setPaymentToEdit] = useState<FamilyPaymentRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FamilyPaymentRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [assignFeeId, setAssignFeeId] = useState<string | null>(null);
  const [forfeitFee, setForfeitFee] = useState<RegistrationFeeView | null>(null);
  const [reverseFee, setReverseFee] = useState<RegistrationFeeView | null>(null);
  const [clearFlagFee, setClearFlagFee] = useState<RegistrationFeeView | null>(null);
  const [closeRefundedFee, setCloseRefundedFee] = useState<RegistrationFeeView | null>(null);
  const [waiveDialogOpen, setWaiveDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const deleteMutation = useDeleteRegistrationPayment();
  const revokeMutation = useRevokeRegistrationFeeWaiver();

  // Gated on both the permission AND a resolved family id (CLAUDE.md:
  // `getFamilyDetails` returns a Parent row — the caller must pass
  // `Family?.id`, never the Parent's own `.id`).
  if (!canRead || !familyId) return null;

  // A fee row to the payment row the amend dialog and delete need. The fee view carries its own
  // reference and notes, so a fee from another year (not in `payments`) amends without losing them.
  const paymentRowForFee = (fee: RegistrationFeeView): FamilyPaymentRow =>
    data?.payments.find((p) => p.id === fee.id) ??
    (data?.registration.payment?.id === fee.id ? data.registration.payment : null) ?? {
      kind: 'REGISTRATION',
      id: fee.id,
      amount: fee.amount,
      processingFee: null,
      method: fee.method,
      paidAt: fee.paidAt,
      createdAt: fee.createdAt,
      reference: fee.reference,
      notes: fee.notes,
      isManual: fee.isManual,
      invoice: null,
    };

  // #374 PER_STUDENT: which children can still have a fee recorded. A live fee is any that is not
  // FORFEITED. With no fees visible (no registration_fees.read) we cannot tell, so list everyone.
  const reg = data?.registration;
  const fees = Array.isArray(reg?.fees) ? reg!.fees : [];
  const allChildren = reg?.children ?? [];
  const perStudent = reg?.scope === 'PER_STUDENT';
  const recordableChildren =
    perStudent && fees.length > 0
      ? allChildren.filter((c) => !fees.some((f) => f.state !== 'FORFEITED' && f.state !== 'REFUNDED' && f.student?.id === c.id))
      : allChildren;
  const showRecordMore =
    perStudent &&
    reg?.status === 'PAID' &&
    // No fees visible: with 0 or 1 child a further record can only 409, so offer it for 2+ only.
    (fees.length === 0 ? allChildren.length >= 2 : recordableChildren.length > 0);

  const handleRecord = () => {
    setPaymentToEdit(null);
    setRecordDialogOpen(true);
  };
  const handleEditPayment = (payment: FamilyPaymentRow) => {
    setPaymentToEdit(payment);
    setRecordDialogOpen(true);
  };
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (error) {
      // Also toasted by the mutation; the credited-fee refusal is explained inline. Dialog stays open.
      if (apiErrorCode(error) === 'REGISTRATION_FEE_CREDITED') {
        const invoiceNumber = apiErrorDetails<{ invoiceNumber?: string }>(error)?.invoiceNumber;
        setDeleteError(
          `This fee has been credited on ${invoiceNumber ? `invoice ${invoiceNumber}` : 'an invoice'}, so it cannot be deleted.`
        );
      }
    }
  };
  const handleRevokeOpen = () => {
    setRevokeReason('');
    setRevokeError(null);
    setRevokeDialogOpen(true);
  };
  const handleRevokeConfirm = async () => {
    if (!familyId) return;
    setRevokeError(null);
    try {
      await revokeMutation.mutateAsync({ familyId, reason: revokeReason.trim() || undefined });
      setRevokeDialogOpen(false);
    } catch (error) {
      setRevokeError(apiErrorMessage(error, 'Failed to revoke the waiver'));
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        <PaymentsIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
        Payment History
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()} sx={{ minHeight: { xs: 44 } }}>
                Retry
              </Button>
            }
          >
            Couldn't load payment history
          </Alert>
        ) : data ? (
          <Stack spacing={2}>
            <RegistrationStatusLine
              registration={data.registration}
              timeZone={timeZone}
              canWrite={canWrite}
              canDelete={canDelete}
              onRecord={handleRecord}
              onWaive={() => setWaiveDialogOpen(true)}
              onEditPayment={handleEditPayment}
              onDeletePayment={setDeleteTarget}
              onRevokeWaiver={handleRevokeOpen}
              hidePaidLine={fees.length > 0}
              showRecordMore={showRecordMore}
            />

            {/* #374: each paid fee with its child and state; [] for a viewer without registration_fees.read. */}
            {Array.isArray(data.registration.fees) && data.registration.fees.length > 0 && (
              <RegistrationFeesList
                fees={data.registration.fees}
                schoolSlug={schoolSlug}
                timeZone={timeZone}
                canAssign={can('registration_fees', 'write')}
                onAssign={(fee) => setAssignFeeId(fee.id)}
                canEdit={canWrite}
                canDelete={canDelete}
                onEdit={(fee) => handleEditPayment(paymentRowForFee(fee))}
                onDelete={(fee) => {
                  setDeleteError(null);
                  setDeleteTarget(paymentRowForFee(fee));
                }}
                canForfeit={can('registration_fees', 'write')}
                onForfeit={setForfeitFee}
                canReverseForfeit={isOwner}
                onReverseForfeit={setReverseFee}
                canClearRefundFlag={isOwner}
                onClearRefundFlag={setClearFlagFee}
                onCloseAsRefunded={setCloseRefundedFee}
              />
            )}

            {/* #372: the family's late-fee exemption (needs invoices write AND pricing delete). */}
            <LateFeeExemptionLine
              familyId={familyId}
              exemption={data.lateFeeExemption}
              timeZone={timeZone}
              canChange={canWrite && can('operations_pricing', 'delete')}
            />

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="family-payment-year-label">Year</InputLabel>
                <Select
                  labelId="family-payment-year-label"
                  value={year}
                  label="Year"
                  onChange={(e) => setYear(e.target.value as number)}
                >
                  {data.years.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                {isFetching && isPlaceholderData && <CircularProgress size={14} />}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Total {year}: {formatCurrencyFromDollars(data.yearTotal)}
                </Typography>
                {data.payments.some((p) => p.method === 'ACCOUNT_CREDIT') && (
                  <Typography variant="caption" color="text.secondary">
                    Account credit is not new money and is not in the total.
                  </Typography>
                )}
              </Box>
            </Box>

            {data.payments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No payments recorded in {year}
              </Typography>
            ) : (
              <Stack divider={<Divider />} spacing={1.5}>
                {data.payments.map((payment) => {
                  const statusColors = payment.invoice ? INVOICE_STATUS_COLORS[payment.invoice.status as keyof typeof INVOICE_STATUS_COLORS] : null;
                  const showInvoiceStatusChip =
                    payment.invoice && payment.invoice.status !== 'PAID' && payment.invoice.status !== 'PARTIALLY_PAID';
                  return (
                    <Box key={`${payment.kind}-${payment.id}`} sx={{ pt: 1.5, '&:first-of-type': { pt: 0 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0, pr: { sm: 1 } }}>
                          {/* The amount alone on its line; the card fee underneath (as the invoice page). */}
                          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {formatCurrencyFromDollars(payment.amount)}
                          </Typography>
                          {paymentDetailText(payment.processingFee) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {paymentDetailText(payment.processingFee)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {PAYMENT_METHOD_LABELS[payment.method]}{' '}
                            <Chip
                              component="span"
                              label={paymentSourceLabel(payment)}
                              size="small"
                              sx={{ height: 18, fontSize: '0.6875rem' }}
                            />
                          </Typography>
                          {/* When it was entered sits with how it came in, so the right column stays narrow. */}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Recorded {formatRecordedAt(payment.createdAt, timeZone)}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {payment.invoice ? (
                              <>
                                <Link component={RouterLink} to={`/${schoolSlug}/admin/invoices/${payment.invoice.id}`}>
                                  Invoice {payment.invoice.invoiceNumber}
                                </Link>
                                {(payment.invoice.description ?? payment.invoice.termName) && (
                                  <> · {payment.invoice.description ?? payment.invoice.termName}</>
                                )}
                                {showInvoiceStatusChip && (
                                  <Chip
                                    label={invoiceStatusLabel(payment.invoice.status)}
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      height: 18,
                                      fontSize: '0.6875rem',
                                      bgcolor: statusColors?.bg,
                                      color: statusColors?.text,
                                    }}
                                  />
                                )}
                              </>
                            ) : (
                              'Registration fee'
                            )}
                          </Typography>
                          {payment.reference && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', overflowWrap: 'anywhere' }}
                            >
                              {payment.reference}
                            </Typography>
                          )}
                          {payment.notes && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', overflowWrap: 'anywhere' }}
                            >
                              {payment.notes}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            textAlign: { xs: 'left', sm: 'right' },
                            flexShrink: { sm: 0 },
                            mt: { xs: 0.5, sm: 0 },
                          }}
                        >
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                            Paid {formatPaymentDay(payment.paidAt, timeZone)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Stack>
        ) : null}
      </Paper>

      <RecordRegistrationPaymentDialog
        open={recordDialogOpen}
        onClose={() => setRecordDialogOpen(false)}
        familyId={familyId}
        defaultAmount={data?.defaultRegistrationFee ?? '50.00'}
        payment={paymentToEdit}
        childOptions={perStudent ? recordableChildren : allChildren}
        sendSoleChild={perStudent}
      />

      <AssignRegistrationFeeDialog
        open={assignFeeId !== null}
        onClose={() => setAssignFeeId(null)}
        familyId={familyId}
        feeId={assignFeeId}
        childOptions={data?.registration.children ?? []}
      />

      <ForfeitRegistrationFeeDialog
        open={forfeitFee !== null}
        onClose={() => setForfeitFee(null)}
        familyId={familyId}
        fee={forfeitFee}
      />

      <ReverseForfeitDialog
        open={reverseFee !== null}
        onClose={() => setReverseFee(null)}
        familyId={familyId}
        fee={reverseFee}
      />

      <ClearRefundFlagDialog
        open={clearFlagFee !== null}
        onClose={() => setClearFlagFee(null)}
        familyId={familyId}
        fee={clearFlagFee}
      />

      <CloseAsRefundedDialog
        open={closeRefundedFee !== null}
        onClose={() => setCloseRefundedFee(null)}
        familyId={familyId}
        fee={closeRefundedFee}
      />

      <WaiveRegistrationFeeDialog
        open={waiveDialogOpen}
        onClose={() => setWaiveDialogOpen(false)}
        familyId={familyId}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Registration Payment"
        message={
          deleteError ??
          'This permanently deletes this registration payment record. This cannot be undone.'
        }
        confirmLabel="Delete"
        confirmColor="error"
        confirmDisabled={deleteError !== null}
        cancelLabel={deleteError !== null ? 'Close' : 'Cancel'}
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
      />

      <Dialog open={revokeDialogOpen} onClose={() => !revokeMutation.isPending && setRevokeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Revoke Waiver</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              The family will owe the registration fee again. This is fully audited and the waiver can be
              re-applied later.
            </Typography>
            <TextField
              fullWidth
              label="Reason (optional)"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              multiline
              rows={2}
              disabled={revokeMutation.isPending}
              inputProps={{ maxLength: 500 }}
            />
            {revokeError && <Alert severity="error">{revokeError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)} disabled={revokeMutation.isPending} sx={{ minHeight: { xs: 44 } }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRevokeConfirm}
            disabled={revokeMutation.isPending}
            startIcon={revokeMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ minHeight: { xs: 44 } }}
          >
            {revokeMutation.isPending ? 'Revoking...' : 'Revoke Waiver'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
